class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.overlay = this.querySelector('.cart-notification__overlay');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onHeaderBagClick = this.handleHeaderBagClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.notification.querySelector('.cart-notification__close')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleLineControlClick.bind(this));
    document.getElementById('cart-icon-bubble')?.addEventListener('click', this.onHeaderBagClick);
  }

  open() {
    this.notification.classList.add('animate', 'active');
    this.overlay?.classList.add('active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    this.overlay?.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  handleHeaderBagClick(evt) {
    if (this.isCartPage()) return;

    evt.preventDefault();
    evt.stopPropagation();
    this.setActiveElement(evt.currentTarget);
    this.openWithCurrentCart();
  }

  isCartPage() {
    const cartPath = new URL(routes.cart_url, window.location.origin).pathname.replace(/\/$/, '');
    const currentPath = window.location.pathname.replace(/\/$/, '');

    return currentPath === cartPath;
  }

  openWithCurrentCart() {
    this.notification.classList.add('is-updating');

    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const params = new URLSearchParams({
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    Promise.all([
      fetch(`${routes.cart_url}?${params.toString()}`).then((response) => response.json()),
      fetch(`${routes.cart_url}.js`).then((response) => response.json()),
    ])
      .then(([sections, cart]) => {
        this.renderSections(sections);
        this.updateHeaderCartCount(cart.item_count);
        this.updateCartTotal(cart.total_price, cart.currency);
        if (this.header) this.header.reveal();
        this.open();
      })
      .catch((error) => console.error(error))
      .finally(() => this.notification.classList.remove('is-updating'));
  }

  renderSections(sections) {
    this.getSectionsToRender().forEach((section) => {
      if (!sections?.[section.id]) return;

      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        sections[section.id],
        section.selector
      );
    });
  }

  renderContents(parsedState) {
    this.renderSections(parsedState.sections);

    this.refreshCartMeta();

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
      },
      {
        id: 'cart-notification-button',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleLineControlClick(evt) {
    const control = evt.target.closest('[data-cart-notification-line] .cart-line-controls__button, [data-cart-notification-remove]');
    if (!control) return;

    const removeLine = control.dataset.cartNotificationRemove;
    const controls = control.closest('[data-cart-notification-line]');
    const line = Number(removeLine || controls?.dataset.cartNotificationLine);
    if (!line) return;

    const quantityElement = controls?.querySelector('.cart-line-controls__quantity');
    const currentQuantity = Number(quantityElement?.textContent || 0);
    const nextQuantity = removeLine ? 0 : Math.max(0, currentQuantity + (control.name === 'plus' ? 1 : -1));

    this.updateLine(line, nextQuantity);
  }

  updateLine(line, quantity) {
    this.notification.classList.add('is-updating');

    fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        this.renderSections(state.sections);

        this.updateHeaderCartCount(state.item_count);
        this.updateCartTotal(state.total_price, state.currency);
      })
      .catch((error) => console.error(error))
      .finally(() => this.notification.classList.remove('is-updating'));
  }

  refreshCartMeta() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.updateHeaderCartCount(cart.item_count);
        this.updateCartTotal(cart.total_price, cart.currency);
      })
      .catch((error) => console.error(error));
  }

  updateHeaderCartCount(itemCount) {
    document.querySelectorAll('[data-header-cart-count]').forEach((countElement) => {
      countElement.textContent = itemCount;
    });
  }

  updateCartTotal(totalPrice, currency) {
    const totalElement = this.querySelector('[data-cart-notification-total]');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(totalPrice, currency);
    }

    this.updateShippingProgress(totalPrice, currency);
  }

  updateShippingProgress(totalPrice, currency) {
    const progressElement = this.querySelector('[data-cart-notification-shipping-progress]');
    if (!progressElement) return;

    const activeCurrency = String(currency || window.Shopify?.currency?.active || '').toUpperCase();
    const activeCountry = String(window.Shopify?.country || '').toUpperCase();
    const total = Number(totalPrice || 0);
    const threshold = 30000;
    const messageStart = 20000;

    if (activeCountry === 'JP' || activeCurrency !== 'USD' || total < messageStart || total <= 0) {
      progressElement.hidden = true;
      progressElement.textContent = '';
      return;
    }

    const isJapanese = (document.documentElement.lang || '').toLowerCase().startsWith('ja');

    if (total >= threshold) {
      progressElement.textContent = isJapanese
        ? '国際送料無料が適用されます。'
        : 'Complimentary international shipping unlocked.';
    } else {
      const remaining = threshold - total;
      const amount = Number.isInteger(remaining / 100) ? (remaining / 100).toFixed(0) : (remaining / 100).toFixed(2);
      const remainingText = `$${amount} USD`;

      progressElement.textContent = isJapanese
        ? `国際送料無料まであと${remainingText}です。`
        : `${remainingText} away from complimentary international shipping.`;
    }

    progressElement.hidden = false;
  }

  formatMoney(cents, currency) {
    const activeCurrency = String(currency || window.Shopify?.currency?.active || 'USD').toUpperCase();

    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: activeCurrency,
        currencyDisplay: 'symbol',
      }).format(cents / 100);

      return `${formatted} ${activeCurrency}`;
    } catch (error) {
      return `$${(cents / 100).toFixed(2)} ${activeCurrency}`;
    }
  }
}

customElements.define('cart-notification', CartNotification);
